import "@xyflow/react/dist/style.css"
import { Background, BackgroundVariant, Controls, type Edge, type FitViewOptions, type Node, type NodeChange, type NodeTypes, ReactFlow, ReactFlowProvider, applyNodeChanges, useReactFlow } from "@xyflow/react"
import { type ReactNode, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useT } from "@/lib/i18n"
import { useTheme } from "@/lib/theme"
import { cn } from "@/lib/utils"

/** Re-fit when the canvas box changes (a side panel opens or the window resizes). */
function FitOnResize({ target, fit }: { target: RefObject<HTMLDivElement | null>; fit: FitViewOptions }) {
  const flow = useReactFlow()
  useEffect(() => {
    const el = target.current
    if (!el) return
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => void flow.fitView({ ...fit, duration: 180 }))
    })
    observer.observe(el)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [flow, target, fit])
  return null
}

/** The one graph canvas: React Flow themed from the console tokens. */
export function GraphCanvas<N extends Node, E extends Edge>({
  nodes,
  edges,
  nodeTypes,
  onNodeClick,
  className,
  children,
  minFitZoom = 0.5,
}: {
  nodes: N[]
  edges: E[]
  nodeTypes: NodeTypes
  onNodeClick?: (node: N) => void
  className?: string
  children?: ReactNode
  /** The smallest zoom fitting may choose; raise it where node text must stay legible. */
  minFitZoom?: number
}) {
  const t = useT()
  const theme = useTheme()
  const box = useRef<HTMLDivElement>(null)
  const fit = useMemo<FitViewOptions>(() => ({ padding: 0.15, maxZoom: 1, minZoom: minFitZoom }), [minFitZoom])
  // Controlled nodes: drags and selection apply to local state, which resets whenever the caller's graph changes.
  const [state, setState] = useState({ source: nodes, local: nodes })
  let current = state
  if (state.source !== nodes) {
    current = { source: nodes, local: nodes }
    setState(current)
  }
  const onNodesChange = useCallback((changes: NodeChange<N>[]) => setState((s) => ({ ...s, local: applyNodeChanges(changes, s.local) })), [])
  return (
    <div ref={box} dir="ltr" className={cn("norn-flow relative h-full w-full", className)}>
      <ReactFlowProvider>
        <ReactFlow<N, E>
          nodes={current.local}
          onNodesChange={onNodesChange}
          edges={edges}
          nodeTypes={nodeTypes}
          colorMode={theme}
          ariaLabelConfig={{
            "node.a11yDescription.default": t("graph.nodeDescription"),
            "node.a11yDescription.keyboardDisabled": t("graph.nodeKeyboardDescription"),
            "node.a11yDescription.ariaLiveMessage": ({ direction, x, y }) => t("graph.nodeMoved", { direction: direction === "left" ? t("graph.moveLeft") : direction === "right" ? t("graph.moveRight") : direction === "up" ? t("graph.moveUp") : direction === "down" ? t("graph.moveDown") : direction, x, y }),
            "edge.a11yDescription.default": t("graph.edgeDescription"),
            "controls.ariaLabel": t("graph.controls"),
            "controls.zoomIn.ariaLabel": t("graph.zoomIn"),
            "controls.zoomOut.ariaLabel": t("graph.zoomOut"),
            "controls.fitView.ariaLabel": t("graph.fitView"),
            "controls.interactive.ariaLabel": t("graph.toggleInteractivity"),
            "minimap.ariaLabel": t("graph.miniMap"),
            "handle.ariaLabel": t("graph.handle"),
          }}
          fitView
          fitViewOptions={fit}
          minZoom={0.15}
          maxZoom={1.75}
          nodesDraggable
          nodesConnectable={false}
          edgesFocusable={false}
          elementsSelectable
          proOptions={{ hideAttribution: true }}
          onNodeClick={onNodeClick ? (_e, node) => onNodeClick(node) : undefined}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
          <Controls showInteractive={false} position="bottom-right" />
          <FitOnResize target={box} fit={fit} />
          {children}
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  )
}

/** Edge style for what the definitions declare (dashed) versus what the ledger recorded (solid). */
export const declaredEdge = { stroke: "var(--subtle-foreground)", strokeWidth: 1.25, strokeDasharray: "5 4" } as const
export const recordedEdge = { stroke: "var(--primary)", strokeWidth: 1.5 } as const

/** The legend chip that says which kind of truth a graph shows. */
export function TruthBadge({ kind }: { kind: "declared" | "recorded" }) {
  const t = useT()
  return (
    <span className="inline-flex h-6 items-center gap-2 rounded-md border border-border-subtle bg-card px-2 text-xs text-muted-foreground">
      <svg width="22" height="6" aria-hidden>
        <line x1="1" y1="3" x2="21" y2="3" style={kind === "declared" ? declaredEdge : recordedEdge} />
      </svg>
      {kind === "declared" ? t("graph.declaredByDefinitions") : t("graph.recordedInLedger")}
    </span>
  )
}
